import React from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { CodeSnippet } from '@newComponents/CodeSnippet'
import { Icon } from '@newComponents/Icon'
import { H3, Paragraph } from '@newComponents/Text'

import { DocSection } from '../shared/DocSection'
import { DocStep } from '../shared/DocStep'
import { InlineCode } from '../shared/InlineCode'

const authorizationUrl =
  'https://guild.host/oauth/authorize?response_type=code&client_id=YOUR_CLIENT_ID&redirect_uri=YOUR_REDIRECT_URI&scope=profile:read&state=RANDOM_CSRF_VALUE'

const pkceAuthorizationUrl =
  'https://guild.host/oauth/authorize?response_type=code&client_id=YOUR_CLIENT_ID&redirect_uri=YOUR_REDIRECT_URI&scope=profile:read%20event_tickets:read&state=RANDOM_CSRF_VALUE&code_challenge=BASE64URL_SHA256_CODE_VERIFIER&code_challenge_method=S256'

const tokenExchange = `curl -X POST https://guild.host/api/oauth/token \\
  -H 'Content-Type: application/x-www-form-urlencoded' \\
  --data-urlencode 'grant_type=authorization_code' \\
  --data-urlencode 'code=AUTHORIZATION_CODE' \\
  --data-urlencode 'client_id=YOUR_CLIENT_ID' \\
  --data-urlencode 'client_secret=YOUR_CLIENT_SECRET' \\
  --data-urlencode 'redirect_uri=YOUR_REDIRECT_URI'`

const pkceTokenExchange = `curl -X POST https://guild.host/api/oauth/token \\
  -H 'Content-Type: application/x-www-form-urlencoded' \\
  --data-urlencode 'grant_type=authorization_code' \\
  --data-urlencode 'code=AUTHORIZATION_CODE' \\
  --data-urlencode 'client_id=YOUR_CLIENT_ID' \\
  --data-urlencode 'code_verifier=ORIGINAL_CODE_VERIFIER' \\
  --data-urlencode 'redirect_uri=YOUR_REDIRECT_URI'`

const tokenResponse = `{
  "access_token": "...",
  "refresh_token": "...",
  "token_type": "Bearer",
  "expires_in": 86400,
  "scope": "profile:read"
}`

const refreshRequest = `curl -X POST https://guild.host/api/oauth/token \\
  -H 'Content-Type: application/x-www-form-urlencoded' \\
  --data-urlencode 'grant_type=refresh_token' \\
  --data-urlencode 'refresh_token=REFRESH_TOKEN' \\
  --data-urlencode 'client_id=YOUR_CLIENT_ID' \\
  --data-urlencode 'client_secret=YOUR_CLIENT_SECRET'`

const userinfoRequest = `curl https://guild.host/api/oauth/userinfo \\
  -H 'Authorization: Bearer ACCESS_TOKEN'`

const eventTicketRequest = `curl https://guild.host/api/next/events/EVENT_SLUG/ticket \\
  -H 'Authorization: Bearer ACCESS_TOKEN'`

const attendeesRequest = `curl 'https://guild.host/api/next/events/EVENT_SLUG/attendees?first=20' \\
  -H 'Authorization: Bearer ACCESS_TOKEN'`

const localDevelopmentValues = `client_id: YOUR_LOCAL_CLIENT_ID
client_secret: YOUR_LOCAL_CLIENT_SECRET
redirect_uri: http://localhost:8081/callback`

const Bullet = ({
  icon,
  children,
}: {
  icon: React.ComponentProps<typeof Icon>['name']
  children: React.ReactNode
}) => (
  <XStack gap='$3' alignItems='flex-start'>
    <Icon name={icon} size='$5' color='$color' flexShrink={0} marginTop={2} />
    <Paragraph size='$5'>{children}</Paragraph>
  </XStack>
)

const OAuth = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Guild supports the OAuth 2.0 authorization code flow for external
            applications that need a Guild user to sign in, grant access, and
            call Guild APIs.
          </Trans>
        </Paragraph>
        <Paragraph size='$5'>
          <Trans>
            Use this guide when you are building a third-party integration that
            connects to Guild. The OAuth flow issues bearer access tokens,
            supports refresh tokens, and supports PKCE for public clients.
          </Trans>
        </Paragraph>
        <YStack gap='$3'>
          <Bullet icon='lock-closed'>
            <Trans>
              Access tokens last 1 day and are sent with{' '}
              <InlineCode>Authorization: Bearer ACCESS_TOKEN</InlineCode>.
            </Trans>
          </Bullet>
          <Bullet icon='arrow-reset'>
            <Trans>Refresh tokens last 30 days and rotate when used.</Trans>
          </Bullet>
          <Bullet icon='clock'>
            <Trans>
              Authorization codes expire after 10 minutes and can only be used
              once.
            </Trans>
          </Bullet>
        </YStack>
      </Card>

      <Card gap='$4'>
        <DocSection id='create-an-oauth-app' title={t`Create an OAuth app`}>
          <Paragraph size='$5'>
            <Trans>
              Create OAuth credentials from an OAuth settings page while signed
              in to Guild. The client secret is only shown once, so store it
              securely when the app is created.
            </Trans>
          </Paragraph>
          <DocStep
            number={1}
            title={t`Open OAuth settings`}
            description={t`For a user app, open /USER_SLUG/settings/oauth. For a Guild app, open /GUILD_SLUG/settings/oauth as a Guild organizer. For an Event app, open /events/EVENT_SLUG/settings/oauth as someone who can manage the Event.`}
          />
          <DocStep
            number={2}
            title={t`Register redirect URIs`}
            description={t`Add every callback URL your integration will use. HTTPS URLs are accepted for production, loopback HTTP URLs are accepted for local development, and custom app schemes are accepted for native apps.`}
          />
          <DocStep
            number={3}
            title={t`Copy the credentials`}
            description={t`Copy the client ID and client secret immediately. Existing OAuth apps show the client ID later, but not the secret.`}
          />
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='redirect-uris' title={t`Redirect URI rules`}>
          <YStack gap='$3'>
            <Bullet icon='checkmark'>
              <Trans>
                Production callbacks should use{' '}
                <InlineCode>https://</InlineCode>.
              </Trans>
            </Bullet>
            <Bullet icon='checkmark'>
              <Trans>
                Local development can use loopback{' '}
                <InlineCode>http://localhost</InlineCode>,{' '}
                <InlineCode>http://127.x.x.x</InlineCode>, or IPv6 loopback
                URLs.
              </Trans>
            </Bullet>
            <Bullet icon='desktop'>
              <Trans>
                Native apps can use custom schemes such as{' '}
                <InlineCode>myapp://oauth/callback</InlineCode>.
              </Trans>
            </Bullet>
            <Bullet icon='alert'>
              <Trans>
                Redirect URIs cannot include URL fragments and cannot use unsafe
                schemes such as <InlineCode>javascript:</InlineCode>,{' '}
                <InlineCode>data:</InlineCode>, <InlineCode>file:</InlineCode>,
                or <InlineCode>blob:</InlineCode>.
              </Trans>
            </Bullet>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection
          id='send-the-user-to-guild'
          title={t`Send the user to Guild`}
        >
          <Paragraph size='$5'>
            <Trans>
              Start the authorization flow by sending the user to Guild with
              <InlineCode>response_type=code</InlineCode>, your{' '}
              <InlineCode>client_id</InlineCode>, a registered{' '}
              <InlineCode>redirect_uri</InlineCode>, requested scopes, and a
              random <InlineCode>state</InlineCode> value.
            </Trans>
          </Paragraph>
          <CodeSnippet code={authorizationUrl} />
          <Paragraph size='$5'>
            <Trans>
              Public clients should use PKCE with{' '}
              <InlineCode>code_challenge_method=S256</InlineCode>.
            </Trans>
          </Paragraph>
          <CodeSnippet code={pkceAuthorizationUrl} />
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='scopes' title={t`Supported scopes`}>
          <YStack gap='$3'>
            <Bullet icon='person'>
              <Trans>
                <InlineCode>profile:read</InlineCode> allows access to the
                authenticated user&apos;s basic profile through the userinfo
                endpoint.
              </Trans>
            </Bullet>
            <Bullet icon='calendar'>
              <Trans>
                <InlineCode>event_tickets:read</InlineCode> allows access to the
                authenticated user&apos;s Event ticket status.
              </Trans>
            </Bullet>
            <Bullet icon='calendar'>
              <Trans>
                <InlineCode>event_attendees:read</InlineCode> allows access to
                Event attendee data when the authorized Guild user can manage
                the requested Event.
              </Trans>
            </Bullet>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='handle-the-callback' title={t`Handle the callback`}>
          <Paragraph size='$5'>
            <Trans>
              After the user allows access, Guild redirects to the registered
              callback URL with <InlineCode>code</InlineCode> and{' '}
              <InlineCode>state</InlineCode>. Validate that the returned state
              matches the value your app originally sent.
            </Trans>
          </Paragraph>
          <CodeSnippet code='YOUR_REDIRECT_URI?code=AUTHORIZATION_CODE&state=RANDOM_CSRF_VALUE' />
          <Paragraph size='$5'>
            <Trans>
              If the user denies access, Guild redirects with{' '}
              <InlineCode>error=access_denied</InlineCode>.
            </Trans>
          </Paragraph>
          <CodeSnippet code='YOUR_REDIRECT_URI?error=access_denied&state=RANDOM_CSRF_VALUE' />
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection
          id='exchange-the-code'
          title={t`Exchange the code for tokens`}
        >
          <Paragraph size='$5'>
            <Trans>
              Exchange the authorization code at the token endpoint. The
              endpoint accepts form-encoded requests and JSON requests, but
              form-encoded is recommended for OAuth clients.
            </Trans>
          </Paragraph>
          <CodeSnippet code={tokenExchange} />
          <Paragraph size='$5'>
            <Trans>
              Public clients using PKCE send the original{' '}
              <InlineCode>code_verifier</InlineCode>. Confidential clients can
              also use PKCE, but should still authenticate with the client
              secret when they can keep it private.
            </Trans>
          </Paragraph>
          <CodeSnippet code={pkceTokenExchange} />
          <CodeSnippet code={tokenResponse} />
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='refresh-tokens' title={t`Refresh tokens`}>
          <Paragraph size='$5'>
            <Trans>
              Use <InlineCode>grant_type=refresh_token</InlineCode> to get a new
              access token. Refresh tokens rotate, so replace the stored refresh
              token with the new one returned by the response.
            </Trans>
          </Paragraph>
          <CodeSnippet code={refreshRequest} />
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='call-guild-apis' title={t`Call Guild APIs`}>
          <YStack gap='$4'>
            <YStack gap='$2'>
              <H3>
                <Trans>User profile</Trans>
              </H3>
              <Paragraph size='$5'>
                <Trans>
                  Use the userinfo endpoint with a bearer access token.
                </Trans>
              </Paragraph>
              <CodeSnippet code={userinfoRequest} />
            </YStack>

            <YStack gap='$2'>
              <H3>
                <Trans>Event ticket check</Trans>
              </H3>
              <Paragraph size='$5'>
                <Trans>
                  To determine whether the signed-in Guild user has a ticket for
                  an Event, call the Event ticket endpoint with{' '}
                  <InlineCode>event_tickets:read</InlineCode>.
                </Trans>
              </Paragraph>
              <CodeSnippet code={eventTicketRequest} />
            </YStack>

            <YStack gap='$2'>
              <H3>
                <Trans>Event attendees</Trans>
              </H3>
              <Paragraph size='$5'>
                <Trans>
                  The Event attendees API requires{' '}
                  <InlineCode>event_attendees:read</InlineCode>, and the
                  authorized Guild user must be able to manage the Event.
                </Trans>
              </Paragraph>
              <CodeSnippet code={attendeesRequest} />
            </YStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='local-development' title={t`Local development`}>
          <Paragraph size='$5'>
            <Trans>
              For local testing, create an OAuth app from your local OAuth
              settings page and register a loopback redirect URI. Keep local
              credentials generic in docs and examples, and do not reuse them in
              production.
            </Trans>
          </Paragraph>
          <CodeSnippet code={localDevelopmentValues} />
        </DocSection>
      </Card>
    </>
  )
}

export default OAuth
